import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Menu,
    MenuItem,
    Stack,
    TextField,
    Typography,
    Chip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import UploadFileIcon from "@mui/icons-material/UploadFile";

import QuestionFormOnlyText from "./question_form/QuestionFormOnlyText";
import QuestionFormTest from "./question_form/QuestionFormTest";
import QuestionFormDropdown from "./question_form/QuestionFormDropdown";
import QuestionFormDropdown2 from "./question_form/QuestionFormDropdown2";
import QuestionFormDnD from "./question_form/QuestionFormDnD";
import QuestionFormDnD2 from "./question_form/QuestionFormDnD2";
import QuestionFormVideo from "./question_form/QuestionFormVideo";
import QuestionFormImageQuiz from "./question_form/QuestionFormImageQuiz";
import QuestionFormInput from "./question_form/QuestionFormInput";
import QuestionFormMatchingPairs from "./question_form/QuestionFormMatchingPairs";
import QuestionFormFillword from "./question_form/QuestionFormFillword";
import QuestionFormPuzzle from "./question_form/QuestionFormPuzzle";
import QuestionFormJigsaw from "./question_form/QuestionFormJigsaw";
import QuestionFormWordGrid from "./question_form/QuestionFormWordGrid";
import QuestionFormLogicalSequence from "./question_form/QuestionFormLogicalSequence";
import QuestionFormPdf from "./question_form/QuestionFormPdf";
import BulkTestQuestionsDialog from "./question_form/BulkTestQuestionsDialog";
import ImagePickerDialog from "./ImagePickerDialog";
import AudioPickerDialog from "./AudioPickerDialog";
import PdfPickerDialog from "./PdfPickerDialog";

const safeStr = (v) => (v == null ? "" : String(v));
const ensureArray = (v) => (Array.isArray(v) ? v : []);
const normalize = (v) => String(v ?? "").trim();
const slugifySection = (value) =>
    normalize(value)
        .toLowerCase()
        .replace(/[^a-z0-9а-яіїєґ]+/gi, "-")
        .replace(/^-+|-+$/g, "");
const normalizeAnswerIds = (answer) =>
    (Array.isArray(answer) ? answer : [answer])
        .map((item) => safeStr(item).trim())
        .filter(Boolean);
const withCommonQuestionMedia = (source, question) => ({
    ...question,
    imgPath: safeStr(source?.imgPath),
    audioPath: safeStr(source?.audioPath),
});

const makeId = () => Math.floor(Math.random() * 1_000_000);
const normalizeSection = (section, idx = 0) => {
    const title = normalize(section?.title);
    const sectionSlug = normalize(section?.sectionSlug || section?.slug) || slugifySection(title) || `section-${idx + 1}`;

    return {
        sectionSlug,
        title: title || sectionSlug,
        description: normalize(section?.description),
        iconKey: normalize(section?.iconKey) || "test",
        order: Number(section?.order ?? idx) || 0,
        enabled: section?.enabled !== false,
    };
};

const detectKind = (q) => {
    if (!q) return "test";

    const t = String(q.type || "").toLowerCase();
    if (t === "text") return "text";
    if (t === "video") return "video";
    if (t === "dnd2") return "dnd2";
    if (t === "dnd") return "dnd";
    if (t === "dropdown") return "dropdown";
    if (t === "dropdown2") return "dropdown2";
    if (t === "imagequiz") return "imageQuiz";
    if (t === "input") return "input";
    if (t === "matchingpairs") return "matchingPairs";
    if (t === "fillword") return "fillword";
    if (t === "puzzle") return "puzzle";
    if (t === "jigsaw") return "jigsaw";
    if (t === "wordgrid") return "wordGrid";
    if (t === "logicalsequence") return "logicalSequence";
    if (t === "pdf") return "pdf";
    if (t === "test") return "test";

    // Legacy fallback for existing data.
    if (q.onlyText) return "text";
    if (q.isGame) return "dnd";
    if (q.type === "interactive" && Array.isArray(q.initialWords)) return "dnd";
    if (q.type === "interactive") return "dropdown";
    if (q.isTest) return "test";

    return "dropdown";
};

const kindLabel = (kind) => {
    if (kind === "text") return "Text";
    if (kind === "test") return "Test";
    if (kind === "dropdown") return "Dropdown";
    if (kind === "dropdown2") return "Dropdown 2.0";
    if (kind === "dnd") return "DnD";
    if (kind === "dnd2") return "DnD 2.0";
    if (kind === "video") return "Video";
    if (kind === "imageQuiz") return "Image Quiz";
    if (kind === "input") return "Завдання з вводом";
    if (kind === "matchingPairs") return "Скласти пари";
    if (kind === "fillword") return "Філворд";
    if (kind === "puzzle") return "Пазл (п'ятнашки)";
    if (kind === "jigsaw") return "Класичний пазл";
    if (kind === "wordGrid") return "Сітка слів";
    if (kind === "logicalSequence") return "Логічна послідовність";
    if (kind === "pdf") return "PDF";
    return "Question";
};

const sanitizeQuestionByKind = (q, kind) => {
    const id = q?.id ?? makeId();

    if (kind === "text") {
        return {
            id,
            type: "text",
            subtitle: safeStr(q?.subtitle),
            text: safeStr(q?.text || "<p>Текст завдання (можна html)...</p>"),
        };
    }

    if (kind === "test") {
        return {
            id,
            question: safeStr(q?.question || "Запитання"),
            options: ensureArray(q?.options).length
                ? ensureArray(q?.options)
                : [
                    { id: "А", text: "Варіант А" },
                    { id: "Б", text: "Варіант Б" },
                    { id: "В", text: "Варіант В" },
                    { id: "Г", text: "Варіант Г" },
                ],
            correctAnswer: safeStr(q?.correctAnswer || "А"),
            type: "test",
            subtitle: safeStr(q?.subtitle),
            imgPath: safeStr(q?.imgPath),
            videoPath: safeStr(q?.videoPath),
            addClass: safeStr(q?.addClass),
        };
    }

    if (kind === "dropdown") {
        return {
            id,
            question: safeStr(q?.question || ""),
            dropdownQuestionParts: ensureArray(q?.dropdownQuestionParts).length
                ? ensureArray(q?.dropdownQuestionParts)
                : ["Тут ", { id: "dropdown1", text: " ..." }, " і ", { id: "dropdown2", text: " ..." }, "."],
            options: ensureArray(q?.options).length
                ? ensureArray(q?.options)
                : [
                    { id: "А", text: "Варіант А" },
                    { id: "Б", text: "Варіант Б" },
                    { id: "В", text: "Варіант В" },
                    { id: "Г", text: "Варіант Г" },
                ],
            correctAnswers:
                q?.correctAnswers && typeof q.correctAnswers === "object" ? q.correctAnswers : { dropdown1: "А", dropdown2: "Б" },
            showAnswerOptions: Boolean(q?.showAnswerOptions ?? true),
            dropdownAmount: Number(q?.dropdownAmount ?? 2) || 0,
            type: "dropdown",
            subtitle: safeStr(q?.subtitle),
        };
    }

    if (kind === "dropdown2") {
        const question = safeStr(q?.question || "Оберіть правильні відповіді\n1) Як тебе не любити [drop] мій\n2) Ім'я поета Шевченка - [drop]");
        const dropAmount = (question.match(/\[drop\]/g) || []).length;
        const sourceDropdowns = ensureArray(q?.dropdowns);

        return {
            id,
            question,
            dropdowns: Array.from({ length: dropAmount }, (_, idx) => {
                const dropdown = sourceDropdowns[idx] || {};
                const options = ensureArray(dropdown.options).map((option) => safeStr(option)).filter(Boolean);

                return {
                    options: options.length ? options : ["Києве", "Тарас", "червоне", "журба"],
                    correctAnswer: safeStr(dropdown.correctAnswer || options[0] || "Києве"),
                };
            }),
            type: "dropdown2",
            subtitle: safeStr(q?.subtitle),
        };
    }

    if (kind === "imageQuiz") {
        const options = ensureArray(q?.options).length
            ? ensureArray(q?.options).map((option, idx) => ({
                id: safeStr(option?.id || ["А", "Б", "В", "Г"][idx] || idx + 1),
                text: safeStr(option?.text),
                imgPath: safeStr(option?.imgPath),
            }))
            : [
                { id: "А", text: "", imgPath: "" },
                { id: "Б", text: "", imgPath: "" },
                { id: "В", text: "", imgPath: "" },
            ];

        return {
            id,
            question: safeStr(q?.question || "На якому зображенні намальований їжачок?"),
            audioPath: safeStr(q?.audioPath),
            options,
            correctAnswer: Array.isArray(q?.correctAnswer)
                ? normalizeAnswerIds(q.correctAnswer)
                : safeStr(q?.correctAnswer || options[0]?.id || "А"),
            type: "imageQuiz",
            isTest: true,
            subtitle: safeStr(q?.subtitle),
        };
    }

    if (kind === "input") {
        const question = safeStr(q?.question || "Вставте пропущене слово\n1) Як тебе не любити [] мій\n2) Ім'я поета Шевченка - []\n3) [] - то любов, а чорне то []");
        const placeholdersAmount = (question.match(/\[(\d*)\]/g) || []).length;
        const sourceAnswers = ensureArray(q?.correctAnswers);

        return {
            id,
            question,
            correctAnswers: Array.from({ length: placeholdersAmount }, (_, idx) => safeStr(sourceAnswers[idx])),
            type: "input",
            subtitle: safeStr(q?.subtitle),
        };
    }

    if (kind === "matchingPairs") {
        const fallbackPairs = [
            {
                id: "pair1",
                left: { text: "Картка 1", imgPath: "" },
                right: { text: "Пара 1", imgPath: "" },
            },
            {
                id: "pair2",
                left: { text: "Картка 2", imgPath: "" },
                right: { text: "Пара 2", imgPath: "" },
            },
        ];
        const sourcePairs = ensureArray(q?.pairs).length ? ensureArray(q?.pairs) : fallbackPairs;

        return {
            id,
            question: safeStr(q?.question || "Поєднай пари."),
            pairs: sourcePairs.map((pair, idx) => ({
                id: safeStr(pair?.id || `pair${idx + 1}`),
                left: {
                    text: safeStr(pair?.left?.text),
                    imgPath: safeStr(pair?.left?.imgPath),
                },
                right: {
                    text: safeStr(pair?.right?.text),
                    imgPath: safeStr(pair?.right?.imgPath),
                },
            })),
            leftTitle: safeStr(q?.leftTitle || "Набір 1"),
            rightTitle: safeStr(q?.rightTitle || "Набір 2"),
            instruction: safeStr(q?.instruction),
            tip: safeStr(q?.tip),
            type: "matchingPairs",
            subtitle: safeStr(q?.subtitle),
        };
    }

    if (kind === "fillword") {
        return {
            id,
            question: safeStr(q?.question || "Знайдіть слова у філворді."),
            imgPath: safeStr(q?.imgPath),
            correctWords: ensureArray(q?.correctWords).length
                ? ensureArray(q?.correctWords).map((word) => safeStr(word))
                : ["слово"],
            type: "fillword",
            subtitle: safeStr(q?.subtitle),
        };
    }

    if (kind === "puzzle") {
        const rows = Number(q?.rows);
        const columns = Number(q?.columns);

        return {
            id,
            question: safeStr(q?.question || "Складіть зображення з частинок."),
            imgPath: safeStr(q?.imgPath),
            rows: [2, 3, 4, 5].includes(rows) ? rows : 3,
            columns: [2, 3, 4, 5].includes(columns) ? columns : 3,
            showPreview: q?.showPreview !== false,
            type: "puzzle",
            subtitle: safeStr(q?.subtitle),
        };
    }

    if (kind === "jigsaw") {
        const rows = Number(q?.rows);
        const columns = Number(q?.columns);

        return {
            id,
            question: safeStr(q?.question || "Складіть зображення з деталей."),
            imgPath: safeStr(q?.imgPath),
            rows: [2, 3, 4].includes(rows) ? rows : 2,
            columns: [2, 3, 4].includes(columns) ? columns : 2,
            showPreview: q?.showPreview !== false,
            type: "jigsaw",
            subtitle: safeStr(q?.subtitle),
        };
    }

    if (kind === "wordGrid") {
        const sourceRows = ensureArray(q?.wordRows);

        return {
            id,
            question: safeStr(q?.question || "Розгадайте слова за підказками."),
            instruction: safeStr(q?.instruction || "Впишіть літери у порожні клітинки. Уже відкриті літери допоможуть зорієнтуватися."),
            wordRows: sourceRows.length
                ? sourceRows.map((row, idx) => ({
                    id: safeStr(row?.id || `row-${idx + 1}`),
                    clue: safeStr(row?.clue),
                    answer: safeStr(row?.answer),
                    offset: Number(row?.offset) || 0,
                }))
                : [
                    { id: "row-1", clue: "Підказка до першого слова", answer: "слово", offset: 0 },
                    { id: "row-2", clue: "Підказка до другого слова", answer: "бу[к]ва", offset: 1 },
                ],
            revealedLetterColor: safeStr(q?.revealedLetterColor || "#c62828"),
            type: "wordGrid",
            subtitle: safeStr(q?.subtitle),
        };
    }

    if (kind === "logicalSequence") {
        const fallbackItems = [
            { id: "А", text: "Більше роби, та менше говори" },
            { id: "Б", text: "Зробив діло - гуляй сміло" },
            { id: "В", text: "Що посієш, те й пожнеш" },
            { id: "Г", text: "Роботящі руки гори вернуть" },
        ];
        const sourceItems = ensureArray(q?.items).length ? ensureArray(q?.items) : fallbackItems;
        const items = sourceItems.map((item, idx) => ({
            id: safeStr(item?.id || ["А", "Б", "В", "Г", "Д", "Е"][idx] || idx + 1),
            text: safeStr(item?.text),
        }));
        const itemIds = items.map((item) => item.id).filter(Boolean);
        const sourceOrder = ensureArray(q?.correctOrder).map(safeStr).filter((itemId) => itemIds.includes(itemId));
        const fallbackOrder = ["В", "А", "Г", "Б"].filter((itemId) => itemIds.includes(itemId));
        const normalizedOrder = sourceOrder.length ? sourceOrder : fallbackOrder;
        const correctOrder = [
            ...normalizedOrder,
            ...itemIds.filter((itemId) => !normalizedOrder.includes(itemId)),
        ];

        return {
            id,
            question: safeStr(q?.question || "Установи логічну послідовність."),
            items,
            correctOrder,
            type: "logicalSequence",
            subtitle: safeStr(q?.subtitle),
        };
    }

    if (kind === "pdf") {
        return {
            id,
            question: safeStr(q?.question || "Перегляньте PDF-матеріал."),
            pdfPath: safeStr(q?.pdfPath),
            pdfFileName: safeStr(q?.pdfFileName),
            type: "pdf",
            subtitle: safeStr(q?.subtitle),
        };
    }

    if (kind === "dnd") {
        return {
            id,
            question: safeStr(q?.question),
            type: "dnd",
            initialWords: ensureArray(q?.initialWords).length
                ? ensureArray(q?.initialWords)
                : [
                    { id: 1, text: "Word 1", type: "leftBucket" },
                    { id: 2, text: "Word 2", type: "rightBucket" },
                ],
            leftBucketTitle: safeStr(q?.leftBucketTitle || "Left bucket"),
            rightBucketTitle: safeStr(q?.rightBucketTitle || "Right bucket"),
            centerBucketTitle: safeStr(q?.centerBucketTitle),
            bucketsAmount: Number(q?.bucketsAmount ?? 2) || 0,
            subtitle: safeStr(q?.subtitle),
        };
    }

    if (kind === "dnd2") {
        const bucketCount = Math.min(4, Math.max(1, Number(q?.bucketCount ?? q?.buckets?.length ?? 2) || 2));
        const bucketSize = [1, 2, 3].includes(Number(q?.bucketSize)) ? Number(q?.bucketSize) : 2;
        const sourceBuckets = ensureArray(q?.buckets);
        const buckets = Array.from({ length: bucketCount }, (_, idx) => {
            const fallbackId = `bucket${idx + 1}`;
            const bucket = sourceBuckets[idx] || {};

            return {
                id: safeStr(bucket.id || fallbackId),
                title: safeStr(bucket.title ?? bucket.label ?? ""),
                backgroundImage: safeStr(bucket.backgroundImage || bucket.backgroundImageUrl),
            };
        });
        const validBucketIds = new Set(buckets.map((bucket) => bucket.id));
        const fallbackBucketId = buckets[0]?.id || "bucket1";

        return {
            id,
            question: safeStr(q?.question),
            type: "dnd2",
            bucketCount,
            bucketSize,
            buckets,
            initialWords: ensureArray(q?.initialWords).length
                ? ensureArray(q?.initialWords).map((word, idx) => ({
                    id: word?.id ?? idx + 1,
                    text: safeStr(word?.text),
                    type: validBucketIds.has(word?.type) ? word.type : fallbackBucketId,
                }))
                : [
                    { id: 1, text: "", type: fallbackBucketId },
                    { id: 2, text: "", type: buckets[1]?.id || fallbackBucketId },
                ],
            subtitle: safeStr(q?.subtitle),
        };
    }

    return {
        id,
        question: safeStr(q?.question || "<b>Watch the video and complete the task.</b>"),
        videoPath: safeStr(q?.videoPath || "https://www.youtube.com/embed/"),
        addClass: safeStr(q?.addClass || "horizontal"),
        shouldGoNext: Boolean(q?.shouldGoNext ?? true),
        type: "video",
        isCreativeTask: Boolean(q?.isCreativeTask ?? false),
        subtitle: safeStr(q?.subtitle),
    };
};

const stripHtml = (s) => safeStr(s).replace(/<[^>]*>/g, "").trim();

const getDnd2ValidationError = (question, idx) => {
    const prefix = `DnD 2.0 #${idx + 1}`;
    const bucketCount = Number(question?.bucketCount);
    const bucketSize = Number(question?.bucketSize);
    const buckets = ensureArray(question?.buckets);
    const words = ensureArray(question?.initialWords);

    if (bucketCount < 1 || bucketCount > 4) return `${prefix}: кількість кошиків має бути від 1 до 4`;
    if (![1, 2, 3].includes(bucketSize)) return `${prefix}: розмір кошиків має бути 1, 2 або 3`;
    if (buckets.length !== bucketCount) return `${prefix}: кількість кошиків не збігається з налаштуванням`;

    const bucketIds = buckets.map((bucket) => safeStr(bucket?.id).trim()).filter(Boolean);
    if (bucketIds.length !== bucketCount) return `${prefix}: кожен кошик має мати ID`;
    if (new Set(bucketIds).size !== bucketIds.length) return `${prefix}: ID кошиків мають бути унікальними`;
    if (words.some((word) => !bucketIds.includes(word?.type))) {
        return `${prefix}: кожне слово має посилатися на наявний кошик`;
    }

    return "";
};

const getImageQuizValidationError = (question, idx) => {
    const prefix = `Image Quiz #${idx + 1}`;
    const options = ensureArray(question?.options);
    const optionIds = options.map((option) => safeStr(option?.id).trim()).filter(Boolean);

    if (!safeStr(question?.question).trim() && !safeStr(question?.audioPath).trim()) {
        return `${prefix}: додайте питання або audioPath`;
    }
    if (options.length < 2 || options.length > 4) {
        return `${prefix}: кількість зображень має бути від 2 до 4`;
    }
    if (optionIds.length !== options.length) {
        return `${prefix}: кожен варіант має мати ID`;
    }
    if (new Set(optionIds).size !== optionIds.length) {
        return `${prefix}: ID варіантів мають бути унікальними`;
    }
    if (options.some((option) => !safeStr(option?.imgPath).trim())) {
        return `${prefix}: кожен варіант має мати URL зображення`;
    }
    const correctAnswers = normalizeAnswerIds(question?.correctAnswer);

    if (!correctAnswers.length || correctAnswers.some((answer) => !optionIds.includes(answer))) {
        return `${prefix}: правильна відповідь має збігатися з ID одного з варіантів`;
    }

    return "";
};

const getDropdown2ValidationError = (question, idx) => {
    const prefix = `Dropdown 2.0 #${idx + 1}`;
    const dropAmount = (safeStr(question?.question).match(/\[drop\]/g) || []).length;
    const dropdowns = ensureArray(question?.dropdowns);

    if (!safeStr(question?.question).trim()) {
        return `${prefix}: додайте текст завдання`;
    }
    if (dropAmount < 1) {
        return `${prefix}: додайте хоча б один [drop]`;
    }
    if (dropdowns.length !== dropAmount) {
        return `${prefix}: кількість списків має збігатися з кількістю [drop]`;
    }

    const invalidIdx = dropdowns.findIndex((dropdown) => {
        const options = ensureArray(dropdown?.options).map((option) => safeStr(option)).filter(Boolean);
        const correctAnswer = safeStr(dropdown?.correctAnswer).trim();
        return options.length < 2 || !correctAnswer || !options.includes(correctAnswer);
    });

    if (invalidIdx >= 0) {
        return `${prefix}: список #${invalidIdx + 1} має мати мінімум 2 варіанти і правильну відповідь з цього списку`;
    }

    return "";
};

const getInputValidationError = (question, idx) => {
    const prefix = `Завдання з вводом #${idx + 1}`;
    const placeholdersAmount = (safeStr(question?.question).match(/\[(\d*)\]/g) || []).length;
    const answers = ensureArray(question?.correctAnswers);

    if (!safeStr(question?.question).trim()) {
        return `${prefix}: додайте текст завдання`;
    }
    if (placeholdersAmount < 1) {
        return `${prefix}: додайте хоча б один [] або [16]`;
    }
    if (answers.length !== placeholdersAmount) {
        return `${prefix}: кількість відповідей має збігатися з кількістю пропусків`;
    }
    if (answers.some((answer) => !safeStr(answer).trim())) {
        return `${prefix}: заповніть усі правильні відповіді`;
    }

    return "";
};

const getMatchingPairsValidationError = (question, idx) => {
    const prefix = `Скласти пари #${idx + 1}`;
    const pairs = ensureArray(question?.pairs);
    const pairIds = pairs.map((pair) => safeStr(pair?.id).trim()).filter(Boolean);

    if (!safeStr(question?.question).trim()) {
        return `${prefix}: додайте текст завдання`;
    }
    if (pairs.length < 2) {
        return `${prefix}: додайте мінімум 2 пари`;
    }
    if (pairIds.length !== pairs.length) {
        return `${prefix}: кожна пара має мати ID`;
    }
    if (new Set(pairIds).size !== pairIds.length) {
        return `${prefix}: ID пар мають бути унікальними`;
    }
    const invalidPairIdx = pairs.findIndex((pair) => {
        const hasLeft = safeStr(pair?.left?.text).trim() || safeStr(pair?.left?.imgPath).trim();
        const hasRight = safeStr(pair?.right?.text).trim() || safeStr(pair?.right?.imgPath).trim();
        return !hasLeft || !hasRight;
    });

    if (invalidPairIdx >= 0) {
        return `${prefix}: пара #${invalidPairIdx + 1} має мати заповнену ліву і праву частину`;
    }

    return "";
};

const getFillwordValidationError = (question, idx) => {
    const prefix = `Філворд #${idx + 1}`;
    const words = ensureArray(question?.correctWords).map((word) => safeStr(word).trim()).filter(Boolean);

    if (!safeStr(question?.imgPath).trim()) {
        return `${prefix}: додайте зображення філворда`;
    }
    if (!words.length) {
        return `${prefix}: додайте хоча б одне правильне слово`;
    }

    return "";
};

const getPuzzleValidationError = (question, idx) => {
    const prefix = `Пазл (п'ятнашки) #${idx + 1}`;

    if (!safeStr(question?.imgPath).trim()) {
        return `${prefix}: додайте зображення.`;
    }

    return "";
};

const getJigsawValidationError = (question, idx) => {
    const prefix = `Класичний пазл #${idx + 1}`;

    if (!safeStr(question?.imgPath).trim()) {
        return `${prefix}: додайте зображення.`;
    }

    return "";
};

const getWordGridValidationError = (question, idx) => {
    const prefix = `Сітка слів #${idx + 1}`;
    const rows = ensureArray(question?.wordRows);
    const filledRows = rows.filter((row) => safeStr(row?.answer).trim());

    if (!filledRows.length) {
        return `${prefix}: додайте хоча б один рядок із відповіддю.`;
    }

    return "";
};

const getLogicalSequenceValidationError = (question, idx) => {
    const prefix = `Логічна послідовність #${idx + 1}`;
    const items = ensureArray(question?.items);
    const itemIds = items.map((item) => safeStr(item?.id).trim()).filter(Boolean);
    const correctOrder = ensureArray(question?.correctOrder).map((item) => safeStr(item).trim()).filter(Boolean);

    if (!safeStr(question?.question).trim()) {
        return `${prefix}: додайте текст завдання.`;
    }
    if (items.length < 2) {
        return `${prefix}: додайте мінімум 2 пункти.`;
    }
    if (itemIds.length !== items.length) {
        return `${prefix}: кожен пункт має мати ID.`;
    }
    if (new Set(itemIds).size !== itemIds.length) {
        return `${prefix}: ID пунктів мають бути унікальними.`;
    }
    if (items.some((item) => !safeStr(item?.text).trim())) {
        return `${prefix}: заповніть текст усіх пунктів.`;
    }
    if (correctOrder.length !== itemIds.length || correctOrder.some((itemId) => !itemIds.includes(itemId))) {
        return `${prefix}: правильна послідовність має містити всі ID пунктів.`;
    }
    if (new Set(correctOrder).size !== correctOrder.length) {
        return `${prefix}: у правильній послідовності не має бути повторів.`;
    }

    return "";
};

const getPdfValidationError = (question, idx) => {
    const prefix = `PDF #${idx + 1}`;

    if (!safeStr(question?.pdfPath).trim()) {
        return `${prefix}: додайте PDF-файл або pdfPath.`;
    }

    return "";
};

const TypeChips = ({ kind, q }) => {
    const chips = [kindLabel(kind)];
    if (kind === "video" && q?.isCreativeTask) chips.push("creative");

    return (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {chips.map((c, idx) => (
                <Chip key={`${c}-${idx}`} label={c} size="small" />
            ))}
        </Stack>
    );
};

const ModuleQuestionsEditorDialog = ({ open, onClose, initialQuestions, initialSections, onSave, title }) => {
    const [questions, setQuestions] = useState([]);
    const [sections, setSections] = useState([]);
    const [selectedIdx, setSelectedIdx] = useState(0);
    const [anchorEl, setAnchorEl] = useState(null);
    const [uploadAnchorEl, setUploadAnchorEl] = useState(null);
    const [bulkTestOpen, setBulkTestOpen] = useState(false);
    const [imagePickerOpen, setImagePickerOpen] = useState(false);
    const [audioPickerOpen, setAudioPickerOpen] = useState(false);
    const [pdfPickerOpen, setPdfPickerOpen] = useState(false);

    useEffect(() => {
        const arr = ensureArray(initialQuestions).map((q) => {
            const kind = detectKind(q);
            return {
                ...withCommonQuestionMedia(q, sanitizeQuestionByKind({ ...q, id: q?.id ?? makeId() }, kind)),
                sectionSlug: normalize(q?.sectionSlug),
            };
        });
        setQuestions(arr);
        setSelectedIdx(0);
        setSections(ensureArray(initialSections).map(normalizeSection));
    }, [initialQuestions, initialSections, open]);

    const selected = useMemo(() => questions[selectedIdx] || null, [questions, selectedIdx]);
    const selectedKind = useMemo(() => detectKind(selected), [selected]);

    const updateSelected = (nextValue) => {
        setQuestions((prev) => prev.map((q, idx) => (idx === selectedIdx ? nextValue : q)));
    };

    const updateSelectedMedia = (patch) => {
        if (!selected) return;
        updateSelected({ ...selected, ...patch });
    };

    const addQuestion = (kind) => {
        const q = sanitizeQuestionByKind({}, kind);
        setQuestions((prev) => [...prev, q]);
        setSelectedIdx(questions.length);
    };

    const addBulkTestQuestions = (nextQuestions) => {
        const normalized = ensureArray(nextQuestions).map((q) => sanitizeQuestionByKind(q, "test"));
        if (!normalized.length) return;

        setQuestions((prev) => [...prev, ...normalized]);
        setSelectedIdx(questions.length);
    };

    const removeSelected = () => {
        setQuestions((prev) => prev.filter((_, idx) => idx !== selectedIdx));
        setSelectedIdx((i) => Math.max(0, i - 1));
    };

    const moveSelected = (dir) => {
        setQuestions((prev) => {
            const i = selectedIdx;
            const j = i + dir;
            if (i < 0 || j < 0 || i >= prev.length || j >= prev.length) return prev;
            const next = [...prev];
            const tmp = next[i];
            next[i] = next[j];
            next[j] = tmp;
            return next;
        });
        setSelectedIdx((i) => i + dir);
    };

    const handleSave = () => {
        const normalized = questions.map((q) => {
            const kind = detectKind(q);
            return {
                ...withCommonQuestionMedia(q, sanitizeQuestionByKind(q, kind)),
                sectionSlug: normalize(q?.sectionSlug),
            };
        });
        const validationError = normalized
            .map((q, idx) => {
                const kind = detectKind(q);
                if (kind === "dnd2") return getDnd2ValidationError(q, idx);
                if (kind === "dropdown2") return getDropdown2ValidationError(q, idx);
                if (kind === "imageQuiz") return getImageQuizValidationError(q, idx);
                if (kind === "input") return getInputValidationError(q, idx);
                if (kind === "matchingPairs") return getMatchingPairsValidationError(q, idx);
                if (kind === "fillword") return getFillwordValidationError(q, idx);
                if (kind === "puzzle") return getPuzzleValidationError(q, idx);
                if (kind === "jigsaw") return getJigsawValidationError(q, idx);
                if (kind === "wordGrid") return getWordGridValidationError(q, idx);
                if (kind === "logicalSequence") return getLogicalSequenceValidationError(q, idx);
                if (kind === "pdf") return getPdfValidationError(q, idx);
                return "";
            })
            .find(Boolean);

        if (validationError) {
            window.alert(validationError);
            return;
        }

        const normalizedSections = sections.map(normalizeSection);
        onSave(normalized, normalizedSections);
    };

    const handleDialogClose = (event, reason) => {
        if (reason === "backdropClick") return;
        onClose(event, reason);
    };

    return (
        <Dialog open={open} onClose={handleDialogClose} fullWidth maxWidth="lg">
            <DialogTitle>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                    <Box>
                        <Typography variant="h6" fontWeight={700}>
                            {title || "Контент модуля"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Питань: {questions.length}
                        </Typography>
                    </Box>

                    <Box>
                        <Button
                            variant="outlined"
                            startIcon={<UploadFileIcon />}
                            onClick={(e) => setUploadAnchorEl(e.currentTarget)}
                            disabled={!selected}
                            sx={{ mr: 1 }}
                        >
                            Завантажити
                        </Button>
                        <Button variant="contained" startIcon={<AddIcon />} onClick={(e) => setAnchorEl(e.currentTarget)}>
                            Додати
                        </Button>
                        <Menu anchorEl={uploadAnchorEl} open={Boolean(uploadAnchorEl)} onClose={() => setUploadAnchorEl(null)}>
                            <MenuItem onClick={() => { setUploadAnchorEl(null); setImagePickerOpen(true); }}>
                                Зображення
                            </MenuItem>
                            <MenuItem onClick={() => { setUploadAnchorEl(null); setAudioPickerOpen(true); }}>
                                Аудіо
                            </MenuItem>
                            <MenuItem
                                disabled={selectedKind !== "pdf"}
                                onClick={() => { setUploadAnchorEl(null); setPdfPickerOpen(true); }}
                            >
                                PDF
                            </MenuItem>
                        </Menu>
                        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                            <MenuItem onClick={() => { setAnchorEl(null); addQuestion("text"); }}>Текст/Презентація</MenuItem>
                            <MenuItem onClick={() => { setAnchorEl(null); addQuestion("test"); }}>Тест</MenuItem>
                            <MenuItem onClick={() => { setAnchorEl(null); setBulkTestOpen(true); }}>Багато тестів</MenuItem>
                            <MenuItem onClick={() => { setAnchorEl(null); addQuestion("dropdown"); }}>Дропдаун</MenuItem>
                            <MenuItem onClick={() => { setAnchorEl(null); addQuestion("imageQuiz"); }}>Фото-вікторина</MenuItem>
                            <MenuItem onClick={() => { setAnchorEl(null); addQuestion("matchingPairs"); }}>Скласти пари</MenuItem>
                            <MenuItem onClick={() => { setAnchorEl(null); addQuestion("fillword"); }}>Філворд</MenuItem>
                            <MenuItem onClick={() => { setAnchorEl(null); addQuestion("jigsaw"); }}>Класичний пазл</MenuItem>
                            <MenuItem onClick={() => { setAnchorEl(null); addQuestion("puzzle"); }}>Пазл (п'ятнашки)</MenuItem>
                            <MenuItem onClick={() => { setAnchorEl(null); addQuestion("wordGrid"); }}>Сітка слів</MenuItem>
                            <MenuItem onClick={() => { setAnchorEl(null); addQuestion("logicalSequence"); }}>Логічна послідовність</MenuItem>
                            <MenuItem onClick={() => { setAnchorEl(null); addQuestion("pdf"); }}>PDF</MenuItem>
                            <MenuItem onClick={() => { setAnchorEl(null); addQuestion("input"); }}>Завдання з вводом</MenuItem>
                            <MenuItem onClick={() => { setAnchorEl(null); addQuestion("dropdown2"); }}>Dropdown 2.0</MenuItem>
                            <MenuItem onClick={() => { setAnchorEl(null); addQuestion("dnd"); }}>DnD</MenuItem>
                            <MenuItem onClick={() => { setAnchorEl(null); addQuestion("dnd2"); }}>DnD 2.0</MenuItem>
                            <MenuItem onClick={() => { setAnchorEl(null); addQuestion("video"); }}>Відео</MenuItem>
                        </Menu>
                    </Box>
                </Stack>
            </DialogTitle>

            <DialogContent>
                <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mt: 1 }}>
                    <Box
                        sx={{
                            width: { xs: "100%", md: 360 },
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 2,
                            overflow: "hidden",
                        }}
                    >
                        <Box sx={{ p: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
                            <Typography variant="subtitle2" fontWeight={700}>
                                Список
                            </Typography>
                        </Box>

                        <Box sx={{ maxHeight: 520, overflow: "auto" }}>
                            {questions.length === 0 ? (
                                <Box sx={{ p: 2 }}>
                                    <Typography color="text.secondary">Порожньо</Typography>
                                </Box>
                            ) : (
                                questions.map((q, idx) => {
                                    const active = idx === selectedIdx;
                                    const kind = detectKind(q);
                                    const preview = kind === "text" ? stripHtml(q?.text).slice(0, 40) : stripHtml(q?.question).slice(0, 40);

                                    return (
                                        <Box
                                            key={q.id || idx}
                                            onClick={() => setSelectedIdx(idx)}
                                            sx={{
                                                p: 1.5,
                                                cursor: "pointer",
                                                borderBottom: "1px solid",
                                                borderColor: "divider",
                                                backgroundColor: active ? "action.selected" : "transparent",
                                            }}
                                        >
                                            <Stack spacing={0.5}>
                                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                    <Typography fontWeight={700} variant="body2">
                                                        #{idx + 1} {kindLabel(kind)}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        id: {q.id}
                                                    </Typography>
                                                </Stack>
                                                <Typography variant="caption" color="text.secondary">
                                                    {preview || "-"}
                                                </Typography>
                                            </Stack>
                                        </Box>
                                    );
                                })
                            )}
                        </Box>
                    </Box>

                    <Box sx={{ flex: 1 }}>
                        {!selected ? (
                            <Box sx={{ p: 2 }}>
                                <Typography color="text.secondary">Додай питання, щоб редагувати</Typography>
                            </Box>
                        ) : (
                            <Stack spacing={2}>
                                <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, p: 2 }}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                                        <Box>
                                            <Typography fontWeight={700}>Питання #{selectedIdx + 1}</Typography>
                                            <TypeChips kind={selectedKind} q={selected} />
                                        </Box>

                                        <Stack direction="row" spacing={1}>
                                            <IconButton size="small" onClick={() => moveSelected(-1)} disabled={selectedIdx <= 0}>
                                                <ArrowUpwardIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" onClick={() => moveSelected(1)} disabled={selectedIdx >= questions.length - 1}>
                                                <ArrowDownwardIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" onClick={removeSelected} disabled={questions.length === 0}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Stack>
                                    </Stack>

                                    <Divider sx={{ my: 2 }} />

                                    <Stack spacing={2}>
                                        <TextField
                                            label="id"
                                            value={safeStr(selected.id)}
                                            onChange={(e) => {
                                                const raw = e.target.value;
                                                const num = Number(raw);
                                                updateSelected({ ...selected, id: Number.isNaN(num) ? raw : num });
                                            }}
                                            fullWidth
                                        />

                                        <TextField
                                            select
                                            label="Секція"
                                            value={safeStr(selected.sectionSlug)}
                                            onChange={(e) => updateSelected({ ...selected, sectionSlug: e.target.value })}
                                            helperText="Необов'язково. Якщо секції не обрано, питання не показуватиметься у секційному режимі."
                                            fullWidth
                                        >
                                            <MenuItem value="">Без секції</MenuItem>
                                            {sections.map((section) => (
                                                <MenuItem key={section.sectionSlug} value={section.sectionSlug}>
                                                    {section.title || section.sectionSlug}
                                                </MenuItem>
                                            ))}
                                        </TextField>

                                        <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, p: 2 }}>
                                            <Stack spacing={1.5}>
                                                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1}>
                                                    <Box>
                                                        <Typography variant="subtitle2" fontWeight={700}>
                                                            Медіа завдання
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Спільні файли для поточного завдання: зображення та аудіо.
                                                        </Typography>
                                                    </Box>
                                                    <Stack direction="row" spacing={1}>
                                                        <Button size="small" variant="outlined" onClick={() => setImagePickerOpen(true)}>
                                                            Зображення
                                                        </Button>
                                                        <Button size="small" variant="outlined" onClick={() => setAudioPickerOpen(true)}>
                                                            Аудіо
                                                        </Button>
                                                    </Stack>
                                                </Stack>

                                                <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
                                                    <TextField
                                                        label="imgPath"
                                                        value={selected.imgPath || ""}
                                                        onChange={(e) => updateSelectedMedia({ imgPath: e.target.value })}
                                                        fullWidth
                                                    />
                                                    <Button
                                                        variant="text"
                                                        color="error"
                                                        onClick={() => updateSelectedMedia({ imgPath: "" })}
                                                        disabled={!selected.imgPath}
                                                    >
                                                        Очистити
                                                    </Button>
                                                </Stack>

                                                {selected.imgPath && (
                                                    <Box
                                                        component="img"
                                                        src={selected.imgPath}
                                                        alt="Question media preview"
                                                        sx={{
                                                            width: "100%",
                                                            maxHeight: 220,
                                                            objectFit: "contain",
                                                            border: "1px solid",
                                                            borderColor: "divider",
                                                            borderRadius: 1,
                                                            bgcolor: "background.default",
                                                        }}
                                                    />
                                                )}

                                                <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
                                                    <TextField
                                                        label="audioPath"
                                                        value={selected.audioPath || ""}
                                                        onChange={(e) => updateSelectedMedia({ audioPath: e.target.value })}
                                                        fullWidth
                                                    />
                                                    <Button
                                                        variant="text"
                                                        color="error"
                                                        onClick={() => updateSelectedMedia({ audioPath: "" })}
                                                        disabled={!selected.audioPath}
                                                    >
                                                        Очистити
                                                    </Button>
                                                </Stack>

                                                {selected.audioPath && (
                                                    <audio controls src={selected.audioPath} style={{ width: "100%" }}>
                                                        Your browser does not support the audio element.
                                                    </audio>
                                                )}
                                            </Stack>
                                        </Box>

                                        {selectedKind === "text" && (
                                            <QuestionFormOnlyText value={selected} onChange={updateSelected} />
                                        )}

                                        {selectedKind === "test" && (
                                            <QuestionFormTest value={selected} onChange={updateSelected} />
                                        )}

                                        {selectedKind === "dropdown" && (
                                            <QuestionFormDropdown value={selected} onChange={updateSelected} />
                                        )}

                                        {selectedKind === "dropdown2" && (
                                            <QuestionFormDropdown2 value={selected} onChange={updateSelected} />
                                        )}

                                        {selectedKind === "input" && (
                                            <QuestionFormInput value={selected} onChange={updateSelected} />
                                        )}

                                        {selectedKind === "imageQuiz" && (
                                            <QuestionFormImageQuiz value={selected} onChange={updateSelected} />
                                        )}

                                        {selectedKind === "matchingPairs" && (
                                            <QuestionFormMatchingPairs value={selected} onChange={updateSelected} />
                                        )}

                                        {selectedKind === "fillword" && (
                                            <QuestionFormFillword value={selected} onChange={updateSelected} />
                                        )}

                                        {selectedKind === "puzzle" && (
                                            <QuestionFormPuzzle value={selected} onChange={updateSelected} />
                                        )}

                                        {selectedKind === "jigsaw" && (
                                            <QuestionFormJigsaw value={selected} onChange={updateSelected} />
                                        )}

                                        {selectedKind === "wordGrid" && (
                                            <QuestionFormWordGrid value={selected} onChange={updateSelected} />
                                        )}

                                        {selectedKind === "logicalSequence" && (
                                            <QuestionFormLogicalSequence value={selected} onChange={updateSelected} />
                                        )}

                                        {selectedKind === "pdf" && (
                                            <QuestionFormPdf value={selected} onChange={updateSelected} />
                                        )}

                                        {selectedKind === "dnd" && (
                                            <QuestionFormDnD value={selected} onChange={updateSelected} />
                                        )}

                                        {selectedKind === "dnd2" && (
                                            <QuestionFormDnD2 value={selected} onChange={updateSelected} />
                                        )}

                                        {selectedKind === "video" && (
                                            <QuestionFormVideo value={selected} onChange={updateSelected} />
                                        )}
                                    </Stack>
                                </Box>

                                <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, p: 2 }}>
                                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                                        JSON (перевірка)
                                    </Typography>
                                    <TextField
                                        value={JSON.stringify(selected, null, 2)}
                                        fullWidth
                                        multiline
                                        minRows={12}
                                        InputProps={{ readOnly: true }}
                                        sx={{ fontFamily: "monospace" }}
                                    />
                                </Box>
                            </Stack>
                        )}
                    </Box>
                </Stack>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose}>Скасувати</Button>
                <Button variant="contained" onClick={handleSave}>
                    Зберегти
                </Button>
            </DialogActions>

            <BulkTestQuestionsDialog
                open={bulkTestOpen}
                onClose={() => setBulkTestOpen(false)}
                onAdd={addBulkTestQuestions}
            />
            <ImagePickerDialog
                open={imagePickerOpen}
                onClose={() => setImagePickerOpen(false)}
                onPick={(img) => {
                    updateSelectedMedia({ imgPath: img.url });
                    setImagePickerOpen(false);
                }}
            />
            <AudioPickerDialog
                open={audioPickerOpen}
                onClose={() => setAudioPickerOpen(false)}
                onPick={(audio) => {
                    updateSelectedMedia({ audioPath: audio.url });
                    setAudioPickerOpen(false);
                }}
            />
            <PdfPickerDialog
                open={pdfPickerOpen}
                onClose={() => setPdfPickerOpen(false)}
                onPick={(pdf) => {
                    if (selected && selectedKind === "pdf") {
                        updateSelected({
                            ...selected,
                            pdfPath: pdf.url,
                            pdfFileName: pdf.originalFilename || pdf.filename || selected.pdfFileName,
                        });
                    }
                    setPdfPickerOpen(false);
                }}
            />
        </Dialog>
    );
};

export default ModuleQuestionsEditorDialog;
